import { get } from 'lodash';
import {
  usePopulateMemo,
  usePopulate,
  useFind,
  useFindRecord,
} from 'src/api/APIHooks';

export const RIDE_HEALTH_REFERRAL_FORM = 'Ride Health Referral Form';

export const configureRideHealthNotes = (notes, rideHealthConfig) => {
  const updatedNotes = notes.map((note) => {
    const newNote = { ...note };
    if (rideHealthConfig && note.text.includes('Ride Health ID:')) {
      const match = note.text.match(/Ride Health ID:\s*(\w+)/);
      const rideId = match ? match[1] : null;
      const rideHealthTrip = `${rideHealthConfig.url}?auth_key=${rideHealthConfig.authKey}` +
        `&firstName=test&lastName=test&email=test&action=ride&ride=${rideId}`;
      const ridehealthTripString = `<a href="${rideHealthTrip}" target="_blank"` +
        'rel="noopener noreferrer">View Trip</a>';
      newNote.text += ` ${ridehealthTripString}`;
    }
    return newNote;
  });
  return updatedNotes;
};

const configureRideHealthForm = (referenceJSON, {
  employee,
  rideHealthConfig,
  caseObj,
  contextModelId,
  contextType,
}) => {
  const email = get(employee, 'email');
  const ELEMENT_NAME_INSTRUCTIONS_HTML = 'questionInstructions';
  const passengerFirstName = get(caseObj, 'person.first_name');
  const passengerLastName = get(caseObj, 'person.last_name');
  const personId = get(caseObj, 'person.id');
  const auth_key = rideHealthConfig.authKey;
  const gcpUrl = rideHealthConfig.url;
  const passback = encodeURIComponent(`${contextType}:${contextModelId}`);
  const rideHealthURL = new URL(gcpUrl);
  const params = {
    auth_key,
    passengerFirstName,
    passengerLastName,
    firstName: get(employee, 'first_name'),
    lastName: get(employee, 'last_name'),
    mrn: personId,
    passback,
    action: 'book',
  };
  if (email) {
    params.email = email;
  }
  rideHealthURL.search = new URLSearchParams(params).toString();
  const instructionEl = referenceJSON.configuration.pages[0].elements.find(
    (e) => e.name === ELEMENT_NAME_INSTRUCTIONS_HTML,
  );
  instructionEl.html = `
    To connect your client with Ride Health, click on this ` +
    `<a target="_new" href=${rideHealthURL}>link</a>. A new ` +
    'browser window will open where you\'ll be prompted to enter ' +
    'the client\'s information. Once you\'ve completed the steps ' +
    'there, return to this page to confirm the Ride Health form was ' +
    'submitted successfully.';
  return referenceJSON;
};

export const useRideHealthConfig = (
  context,
  contextType,
  rideHealthLinkMapping,
) => {
  try {
    if (contextType !== 'case' || !context) {
      return {
        rideHealthConfig: null,
        eligibleForRideHealth: false,
        caseObj: null,
        isLoading: false,
      };
    }
    const caseQuery = useFindRecord('case', context, {
      queryConfig: {
        enabled: Boolean(contextType === 'case' && context),
        placeholderData: undefined,
      },
    });
    const {
      data: caseResponse,
      isLoading: isLoadingCase,
    } = caseQuery;
    const caseObj = get(caseResponse, 'data.data');
    const personQuery = usePopulate('person', 'person', caseObj, {
      queryConfig: {
        enabled: Boolean(caseObj),
        placeholderData: undefined,
      },
    });
    const { isLoading: isLoadingPerson } = personQuery;

    const person = get(caseObj, 'person.id');
    const { data: insurancesData } = useFind(
      'insurance',
      { person, state: 'active', 'plan.plan_type': 'social' },
      {
        queryConfig: {
          enabled: Boolean(person),
          placeholderData: undefined,
        },
      },
    );

    usePopulateMemo(caseQuery.data, [personQuery]);

    const list = get(insurancesData, 'data.data', []);
    const socialCareCoveragePlan = list.find((insurance) => rideHealthLinkMapping[insurance?.plan?.id]);
    const rideHealthConfig = rideHealthLinkMapping[socialCareCoveragePlan?.plan?.id];

    if (rideHealthConfig) {
      rideHealthConfig.baseUrl = rideHealthConfig.url.split('/').slice(0, -1).join('/');
    }
    return {
      rideHealthConfig,
      eligibleForRideHealth: Boolean(rideHealthConfig),
      caseObj,
      isLoading: isLoadingPerson || isLoadingCase,
    };
  } catch (error) {
    // console.error('useRideHealthConfig', error);
    return {
      rideHealthConfig: null,
      eligibleForRideHealth: false,
      caseObj: null,
      isLoading: false,
    };
  }
};

const useRideHealth = ({
  employee,
  contextModelId,
  contextType,
  rideHealthLinkMapping,
  surveyJSON,
}) => {
  try {
    const {
      isLoading,
      rideHealthConfig,
      caseObj,
    } = useRideHealthConfig(
      contextModelId,
      contextType,
      rideHealthLinkMapping,
    );
    const updatedSurveyJSON = rideHealthConfig ? configureRideHealthForm(
      surveyJSON,
      {
        employee,
        rideHealthConfig,
        caseObj,
        contextModelId,
        contextType,
      },
    ) : null;

    return {
      isLoading,
      isReady: Boolean(rideHealthConfig),
      caseObj,
      rideHealthConfig,
      updatedSurveyJSON,
    };
  } catch (error) {
    window.datadogLogs.logger.error('useRideHealth', {
      error,
    });
    return {
      isLoading: false,
      isReady: false,
      caseObj: null,
      rideHealthConfig: null,
      updatedSurveyJSON: null,
    };
  }
};

export default useRideHealth;
